import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement,
  BarElement,
} from 'chart.js';
import { DashboardData, CalculatedData } from './types';
import SettingsPanel from './components/SettingsPanel';
import Charts from './components/Charts';
import MetricsCards from './components/MetricsCards';
import { calculateDashboardData } from './utils';

// Functions呼び出しの型定義
interface CalculateDashboardResponse {
  success: boolean;
  data?: CalculatedData;
  error?: string;
}
// 2nd Phaseで使用予定
// import { saveCustomerData } from './firebase/firestore';
// import { CustomerData } from './firebase/types';

// Chart.jsの登録
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  ArcElement,
  BarElement
);

const LegacyDashboard: React.FC = () => {
  // デフォルトの入力値
  const defaultData: DashboardData = {
    companyName: '株式会社サンプル',
    generalHiring: 50,
    techHiring: 30,
    budget: 500,
    companySize: 0.6,
    industry: 1.2,
    salary: 25.0,
    stability: 4.2,
    growth: 4.0,
    organicRatio: 42,
    snsFollowers: 8500,
    pagePV: 8000,
    rating: 5.0,
    brandVisibility: 3.2,
    effectiveJobSeekingRate: 45.8,
    prMeasures: 4,
    regionalFactor: 1.0,
    workLifeBalance: 3.0,
    technicalCapability: 3.0,
  };

  const [data, setData] = useState<DashboardData>(defaultData);

  // デフォルトの入力値から計算した値を初期値として設定
  const [calculatedData, setCalculatedData] = useState<CalculatedData>(
    calculateDashboardData(defaultData)
  );

  // 2nd Phaseで使用予定
  // const [isSaving, setIsSaving] = useState(false);
  // const [saveMessage, setSaveMessage] = useState('');

  const [calculationError, setCalculationError] = useState('');
  const [lastInputType, setLastInputType] = useState<
    'slider' | 'input' | 'select'
  >('input');

  // Functions呼び出しで計算ロジック（デバウンス付き）
  useEffect(() => {
    const calculateWithFunctions = async () => {
      setCalculationError('');

      try {
        const response = await fetch(
          'https://calculatedashboard-npl66qeuna-uc.a.run.app',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = (await response.json()) as CalculateDashboardResponse;

        if (result.success && result.data) {
          // FunctionsのレスポンスにeffectiveJobSeekingRateが含まれていない場合は、クライアント側で計算
          const functionsData = result.data;
          const clientCalculated = calculateDashboardData(data);
          setCalculatedData({
            ...functionsData,
            // FunctionsのレスポンスにeffectiveJobSeekingRateが含まれていない場合は、クライアント側で計算した値を使用
            effectiveJobSeekingRate:
              functionsData.effectiveJobSeekingRate ??
              clientCalculated.effectiveJobSeekingRate,
            // FunctionsのレスポンスにbrandVisibilityが含まれていない場合は、クライアント側で計算した値を使用
            brandVisibility:
              functionsData.brandVisibility ?? clientCalculated.brandVisibility,
          });
        } else {
          setCalculationError(result.error || '計算に失敗しました');
        }
      } catch (error) {
        // console.error('Functions呼び出しエラー:', error);

        // より詳細なエラーメッセージ
        let errorMessage = 'サーバーとの通信に失敗しました';
        if (error instanceof Error) {
          if (error.message.includes('functions/not-found')) {
            errorMessage =
              'Functionsが見つかりません。デプロイを確認してください。';
          } else if (error.message.includes('permission-denied')) {
            errorMessage = 'アクセス権限がありません。';
          } else if (error.message.includes('unavailable')) {
            errorMessage =
              'サーバーが利用できません。しばらく待ってから再試行してください。';
          } else {
            errorMessage = `エラー: ${error.message}`;
          }
        }

        setCalculationError(errorMessage);
      }
    };

    // デバウンス: 入力タイプに応じて時間を調整
    const debounceTime = lastInputType === 'slider' ? 1500 : 500;
    const timeoutId = setTimeout(calculateWithFunctions, debounceTime);

    return () => clearTimeout(timeoutId);
  }, [data, lastInputType]);

  const handleInputChange = (
    field: keyof DashboardData,
    value: string | number | boolean,
    inputType: 'slider' | 'input' | 'select' = 'input'
  ) => {
    setLastInputType(inputType);
    setData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    if (field === 'prMeasures') {
      const prCheckboxes = ['employeeRef', 'prVideo', 'snsActive', 'jobFair'];
      const currentCount = prCheckboxes.reduce((count, id) => {
        const checkbox = document.getElementById(id) as HTMLInputElement;
        return count + (checkbox?.checked ? 1 : 0);
      }, 0);
      const newCount = checked ? currentCount + 1 : currentCount - 1;
      setData(prev => ({ ...prev, prMeasures: newCount }));
    } else if (field === 'regionalFactor') {
      const regionCheckboxes = [
        'tokyo',
        'osaka',
        'aichi',
        'kanagawa',
        'fukuoka',
      ];
      const currentCount = regionCheckboxes.reduce((count, id) => {
        const checkbox = document.getElementById(id) as HTMLInputElement;
        return count + (checkbox?.checked ? 1 : 0);
      }, 0);
      const newCount = checked ? currentCount + 1 : currentCount - 1;
      const regionalFactor = 1.0 + (newCount - 1) * 0.1;
      setData(prev => ({ ...prev, regionalFactor }));
    }
  };

  // データ保存関数 - 2nd Phaseで使用予定
  /*
  const handleSaveData = async () => {
    if (!data.companyName.trim()) {
      setSaveMessage('会社名を入力してください');
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const customerData: CustomerData = {
        companyName: data.companyName,
        generalHiring: data.generalHiring,
        techHiring: data.techHiring,
        budget: data.budget,
        companySize: data.companySize,
        industry: data.industry,
        salary: data.salary,
        stability: data.stability,
        growth: data.growth,
        organicRatio: data.organicRatio,
        snsFollowers: data.snsFollowers,
        pagePV: data.pagePV,
        rating: data.rating,
        brandVisibility: calculatedData.brandVisibility || 0,
        effectiveJobSeekingRate: calculatedData.effectiveJobSeekingRate || 0,
        prMeasures: data.prMeasures,
        regionalFactor: data.regionalFactor,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Saving customer data:', customerData);
      const docId = await saveCustomerData(customerData);
      console.log('Data saved successfully with ID:', docId);
      setSaveMessage(`データを保存しました (ID: ${docId})`);
    } catch (error) {
      console.error('データ保存エラー:', error);
      setSaveMessage(`データの保存に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };
  */

  return (
    <div className="container mx-auto px-4 py-6">
      {/* ヘッダー */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">
          採用戦略分析ダッシュボード 2025
        </h1>
        <p className="text-xl text-white opacity-90">
          競合分析・ブランドスコア統合分析システム
        </p>
      </div>

      {/* 会社名入力 - 2nd Phaseで有効化予定 */}
      {/* 
      <div className="dashboard-card p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">会社名</label>
        <input 
          type="text" 
          value={data.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg"
        />
        
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={handleSaveData}
            disabled={isSaving}
            className={`px-6 py-2 rounded-lg font-medium ${
              isSaving 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSaving ? '保存中...' : 'データを保存'}
          </button>
          
          {saveMessage && (
            <span className={`text-sm ${
              saveMessage.includes('失敗') ? 'text-red-600' : 'text-green-600'
            }`}>
              {saveMessage}
            </span>
          )}
        </div>
      </div>
      */}

      {calculationError && (
        <div className="dashboard-card p-6 mb-6 bg-red-50 border border-red-200">
          <div className="text-center">
            <div className="text-lg text-red-600">エラーが発生しました</div>
            <div className="text-sm text-red-500">{calculationError}</div>
          </div>
        </div>
      )}

      {/* メトリクスカード */}
      <MetricsCards data={data} calculatedData={calculatedData} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 設定パネル */}
        <SettingsPanel
          data={data}
          onInputChange={handleInputChange}
          onCheckboxChange={handleCheckboxChange}
        />

        {/* 分析結果 */}
        <Charts data={data} calculatedData={calculatedData} />
      </div>

      {/* フッター */}
      <div className="text-center mt-12 text-white opacity-75 text-sm">
        © 2025 採用可能母集団試算システム -
        データソース：厚生労働省・文部科学省統計データ
      </div>
    </div>
  );
};

export default LegacyDashboard;
